import React, { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { useLang } from "../contexts/LangContext";
import { TREATMENTS } from "./seo/content";
import { ArrowUpRight, Clock, Tag, MapPin, Check } from "lucide-react";

/**
 * Renders a lightweight subset: **bold**, [text](url) links.
 * Same helper style as BlogPost — kept inline to keep the tree small.
 */
function renderInline(text) {
  const nodes = [];
  let rest = text;
  let i = 0;
  const linkRe = /\[([^\]]+)\]\(([^)]+)\)/;
  const boldRe = /\*\*([^*]+)\*\*/;
  while (rest.length) {
    const l = rest.match(linkRe);
    const b = rest.match(boldRe);
    const lIdx = l ? l.index : Infinity;
    const bIdx = b ? b.index : Infinity;
    if (lIdx === Infinity && bIdx === Infinity) {
      nodes.push(<React.Fragment key={i++}>{rest}</React.Fragment>);
      break;
    }
    if (lIdx < bIdx) {
      nodes.push(<React.Fragment key={i++}>{rest.substring(0, lIdx)}</React.Fragment>);
      nodes.push(
        <Link key={i++} to={l[2]} className="text-terracotta underline underline-offset-2 hover:no-underline">
          {l[1]}
        </Link>
      );
      rest = rest.substring(lIdx + l[0].length);
    } else {
      nodes.push(<React.Fragment key={i++}>{rest.substring(0, bIdx)}</React.Fragment>);
      nodes.push(
        <strong key={i++} className="text-ink font-medium">
          {b[1]}
        </strong>
      );
      rest = rest.substring(bIdx + b[0].length);
    }
  }
  return nodes;
}

/**
 * Schema.org helper — injects Service + FAQPage JSON-LD into <head>.
 * On unmount removes them so switching routes stays clean.
 */
function useTreatmentSchema(treatment, lang) {
  useEffect(() => {
    if (!treatment) return;
    const t = treatment[lang] || treatment.de;
    const serviceLd = {
      "@context": "https://schema.org",
      "@type": "Service",
      name: t.h1,
      description: t.metaDescription,
      provider: {
        "@type": "HealthAndBeautyBusiness",
        name: "Ayurveda Aargau",
        telephone: "+41 78 265 09 99",
        address: [
          {
            "@type": "PostalAddress",
            streetAddress: "Obere Feldeggstrasse 13",
            postalCode: "5614",
            addressLocality: "Sarmenstorf",
            addressRegion: "AG",
            addressCountry: "CH",
          },
          {
            "@type": "PostalAddress",
            streetAddress: "Aarestrasse 20",
            postalCode: "5102",
            addressLocality: "Rupperswil",
            addressRegion: "AG",
            addressCountry: "CH",
          },
        ],
      },
      areaServed: { "@type": "AdministrativeArea", name: "Kanton Aargau" },
      offers: {
        "@type": "Offer",
        price: String(treatment.priceChf),
        priceCurrency: "CHF",
        availability: "https://schema.org/InStock",
      },
    };
    const faqLd = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: t.faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    };
    const insertScript = (id, data) => {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.id = id;
      s.text = JSON.stringify(data);
      document.head.appendChild(s);
      return s;
    };
    const a = insertScript("ld-treatment-service", serviceLd);
    const b = insertScript("ld-treatment-faq", faqLd);
    return () => {
      a.remove();
      b.remove();
    };
  }, [treatment, lang]);
}

export default function TreatmentDetail() {
  const { slug } = useParams();
  const { lang } = useLang();
  const treatment = TREATMENTS[slug];
  useTreatmentSchema(treatment, lang);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!treatment) return <Navigate to="/massagen" replace />;
  const t = treatment[lang] || treatment.de;

  return (
    <div data-testid={`treatment-${slug}`}>
      {/* Hero */}
      <section className="bg-sand-soft border-b border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20 lg:py-28">
          <div className="max-w-3xl">
            <div className="text-xs uppercase tracking-[0.3em] text-terracotta mb-5">— Behandlung</div>
            <h1 className="font-display font-light text-4xl lg:text-6xl text-ink leading-tight">{t.h1}</h1>
            <p className="mt-8 text-lg text-ink-muted leading-relaxed max-w-2xl">{t.lead}</p>

            <div className="mt-10 flex flex-wrap gap-6 items-center">
              <div className="inline-flex items-center gap-2 text-ink">
                <Clock className="w-4 h-4 text-terracotta" strokeWidth={1.5} />
                <span className="text-sm">{treatment.durationMin} Min</span>
              </div>
              <div className="inline-flex items-center gap-2 text-ink">
                <Tag className="w-4 h-4 text-terracotta" strokeWidth={1.5} />
                <span className="text-sm font-display text-lg text-terracotta">CHF {treatment.priceChf}</span>
              </div>
              <div className="inline-flex items-center gap-2 text-ink">
                <MapPin className="w-4 h-4 text-terracotta" strokeWidth={1.5} />
                <span className="text-sm">Sarmenstorf · Rupperswil</span>
              </div>
            </div>

            <div className="mt-8">
              <Link
                to="/booking"
                className="inline-flex items-center gap-2 px-8 py-4 bg-terracotta text-white rounded-full hover:bg-terracotta-hover transition-colors font-medium"
                data-testid={`treatment-${slug}-book-top`}
              >
                Jetzt Termin buchen
                <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Sections */}
      <div className="max-w-4xl mx-auto px-6 lg:px-12 py-16">
        {t.sections.map((sec, i) => (
          <section key={i} className="mb-14">
            <h2 className="font-display text-3xl lg:text-4xl text-ink leading-tight mb-6">{sec.h2}</h2>
            {sec.body.map((p, j) => (
              <p key={j} className="text-ink-muted leading-relaxed mb-4">
                {renderInline(p)}
              </p>
            ))}
            {sec.list && (
              <ul className="mt-4 space-y-3">
                {sec.list.map((li, k) => (
                  <li key={k} className="flex gap-3 text-ink-muted leading-relaxed">
                    <span className="w-5 h-5 rounded-full bg-terracotta/10 text-terracotta flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-3 h-3" strokeWidth={2.5} />
                    </span>
                    <span className="text-sm">{renderInline(li)}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        ))}
      </div>

      {/* FAQ */}
      <section className="bg-sand-soft border-t border-border">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 py-16">
          <h2 className="font-display text-3xl lg:text-4xl text-ink leading-tight">Häufige Fragen</h2>
          <div className="mt-10 space-y-6">
            {t.faqs.map((f, i) => (
              <div key={i} className="bg-white border border-border rounded-md p-6">
                <h3 className="font-display text-xl text-ink mb-2">{f.q}</h3>
                <p className="text-sm text-ink-muted leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
          <div className="mt-12">
            <Link
              to="/booking"
              className="inline-flex items-center gap-2 px-8 py-4 bg-terracotta text-white rounded-full hover:bg-terracotta-hover transition-colors font-medium"
              data-testid={`treatment-${slug}-book-bottom`}
            >
              Jetzt Termin buchen
              <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
